import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import type {
  Clubhouse_LeagueRoundV2Dto,
  Clubhouse_RoundSetting,
} from "../../types/leagues";
import type { SeasonRoundItem } from "../leagueFormSchema";
import { eligibleLaterRounds } from "./roundSettingsPropagation";
import { useApplyRoundSettings } from "./useApplyRoundSettings";

interface UseApplyRoundSettingsPromptArgs {
  facilityId: string | undefined;
  leagueId: string | undefined;
  /** The league's rounds (from getLeagueRounds), used to find eligible targets. */
  rounds: Clubhouse_LeagueRoundV2Dto[] | undefined;
  /** Season Flex: a round's play window is not enforced, so a stale one must
   *  not exclude it from the targets. */
  isFlex?: boolean;
  /** Facility course list, used to resolve each target's slugged course id. */
  courses?: { id: number; displayName: string }[];
}

/**
 * Wires up the "apply this round's settings to later rounds" prompt shared by
 * every round-edit entry point (RoundDetailPage, ScheduleDatesTab,
 * LeagueDetailPage). Call `maybePrompt` after a successful round edit; spread
 * `dialogProps` onto <ApplyRoundSettingsDialog />. Keeps the trigger rule, the
 * apply call, and the result toasts in one place.
 */
export function useApplyRoundSettingsPrompt({
  facilityId,
  leagueId,
  rounds,
  isFlex,
  courses,
}: UseApplyRoundSettingsPromptArgs) {
  const { apply, isApplying } = useApplyRoundSettings();
  const [prompt, setPrompt] = useState<{
    targets: Clubhouse_LeagueRoundV2Dto[];
    sourceSettings: Clubhouse_RoundSetting;
  } | null>(null);

  /**
   * Open the prompt after editing a round, but only when there's something to
   * offer: the edited round has settings to copy and at least one later round is
   * still editable. A settings-less edit or a last-round edit is a no-op.
   */
  const maybePrompt = useCallback(
    (editedRoundId: string, settings: SeasonRoundItem["settings"]) => {
      if (!settings || !rounds) return;
      const currentRoundNumber = rounds.find(
        (r) => String(r.id) === editedRoundId,
      )?.roundNumber;
      if (currentRoundNumber == null) return;
      const targets = eligibleLaterRounds(rounds, currentRoundNumber, isFlex);
      if (targets.length === 0) return;
      // The form's settings are a superset (they carry the frontend-only
      // selectedHoles); the propagation only reads the shared fields.
      setPrompt({
        targets,
        sourceSettings: settings as Clubhouse_RoundSetting,
      });
    },
    [rounds, isFlex],
  );

  const handleApply = useCallback(async () => {
    if (!prompt || !facilityId || !leagueId) return;
    const { applied, failed } = await apply({
      facilityId,
      leagueId,
      targets: prompt.targets,
      sourceSettings: prompt.sourceSettings,
      courses,
    });
    setPrompt(null);
    if (failed === 0) {
      toast.success(
        `Settings applied to ${applied} ${applied === 1 ? "round" : "rounds"}.`,
      );
    } else if (applied === 0) {
      toast.error(
        "Couldn't apply settings to the other rounds. Please try again.",
      );
    } else {
      toast.error(
        `Applied to ${applied} of ${applied + failed} rounds; some couldn't be updated.`,
      );
    }
  }, [prompt, facilityId, leagueId, apply, courses]);

  const dialogProps = {
    open: !!prompt,
    onOpenChange: (open: boolean) => {
      if (!open) setPrompt(null);
    },
    targetRounds: prompt?.targets ?? [],
    onApply: handleApply,
    isApplying,
  };

  return { maybePrompt, dialogProps };
}
